/**
 * Meta Pixel and CAPI tracking utilities
 *
 * Handles:
 * - fbclid capture from URL → _fbc cookie
 * - fbp generation → _fbp cookie
 * - Cross-subdomain cookie sharing between bigdevsoon.me and app.bigdevsoon.me
 */

import { META_COOKIE_FBC, META_COOKIE_FBP, META_URL_PARAM_FBCLID } from 'config/constants/github';

import { getCookie, setCookieWithDomain } from './cookies';
import { isLocalEnvironment } from './env';

// Cookie lifetime in days (Meta standard: 90 days)
const META_COOKIE_MAX_AGE_DAYS = 90;

// Root domain for cross-subdomain cookies
const ROOT_DOMAIN = '.bigdevsoon.me';

/**
 * Get the root domain for cookies.
 * Returns '.bigdevsoon.me' only for production domains.
 * Returns undefined for staging/other domains (browser uses current host).
 * Returns current hostname for localhost.
 */
const getCookieDomain = (): string | undefined => {
  const hostname = window.location.hostname;

  // localhost or IP - use current host (no leading dot)
  if (hostname === 'localhost' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return hostname;
  }

  // Production domains - use root domain for cross-subdomain sharing
  if (hostname.endsWith('bigdevsoon.me')) {
    return ROOT_DOMAIN;
  }

  // Staging/other domains (e.g., vercel.app) - don't specify domain
  // Browser will use current hostname automatically
  return undefined;
};

/**
 * Capture fbclid from URL and store as _fbc cookie.
 * Format: fb.1.{timestamp}.{fbclid}
 *
 * Call this on page load to capture fbclid from Meta ad clicks.
 * The cookie is set on .bigdevsoon.me to be shared between landing and app.
 */
export const captureFbclid = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const fbclid = urlParams.get(META_URL_PARAM_FBCLID);

  if (!fbclid) return;

  // Format: fb.1.{creation_time}.{fbclid}
  // version=1, subdomainIndex=1
  const fbc = `fb.1.${Date.now()}.${fbclid}`;

  setCookieWithDomain(META_COOKIE_FBC, fbc, getCookieDomain(), META_COOKIE_MAX_AGE_DAYS);

  // Clean URL by removing fbclid parameter
  const url = new URL(window.location.href);
  url.searchParams.delete(META_URL_PARAM_FBCLID);
  window.history.replaceState({}, '', url.toString());
};

/**
 * Generate _fbp cookie if not exists.
 * Format: fb.1.{timestamp}.{random}
 *
 * This identifies the browser for Meta tracking.
 * Call after user gives consent for marketing cookies.
 */
export const generateFbp = (): void => {
  const existingFbp = getCookie(META_COOKIE_FBP);
  if (existingFbp) return;

  // Format: fb.1.{creation_time}.{random_number}
  const random = Math.floor(Math.random() * 10000000000);
  const fbp = `fb.1.${Date.now()}.${random}`;

  setCookieWithDomain(META_COOKIE_FBP, fbp, getCookieDomain(), META_COOKIE_MAX_AGE_DAYS);
};

/**
 * Get current _fbc cookie value
 */
export const getFbc = (): string | null => {
  return getCookie(META_COOKIE_FBC);
};

/**
 * Get current _fbp cookie value
 */
export const getFbp = (): string | null => {
  return getCookie(META_COOKIE_FBP);
};

/**
 * Get both Meta tracking cookies for sending to backend
 */
export const getMetaTrackingData = (): { fbc: string | null; fbp: string | null } => {
  return {
    fbc: getFbc(),
    fbp: getFbp(),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Client-side Pixel (for PageView tracking with consent)
// ─────────────────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fbq: any; // Meta Pixel uses unconventional patterns that don't fit strict typing
    _fbq: unknown;
  }
}

const META_PIXEL_ID = '3641031172877437';

/**
 * Initialize Meta Pixel (call after consent is given)
 * The Pixel script is loaded dynamically to respect consent.
 */
export const initMetaPixel = (): void => {
  if (!META_PIXEL_ID) {
    console.log('[META PIXEL] Skipped: Missing PIXEL_ID');
    return;
  }

  if (typeof window.fbq !== 'undefined' && window.fbq.loaded) {
    return; // Already initialized
  }

  // Standard Meta Pixel base code (matches official snippet)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const n: any = (window.fbq = function (...args: unknown[]) {
    if (n.callMethod) {
      n.callMethod(...args);
    } else {
      n.queue.push(args);
    }
  });

  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);

  window.fbq('init', META_PIXEL_ID);
  console.log('[META PIXEL] Initialized');
};

/**
 * Track PageView event (call after Pixel is initialized)
 */
export const trackPixelPageView = (): void => {
  if (typeof window.fbq === 'undefined') return;
  window.fbq('track', 'PageView');
};

/**
 * Track custom event via Pixel
 */
export const trackPixelEvent = (eventName: string, params?: Record<string, unknown>): void => {
  if (typeof window.fbq === 'undefined') {
    if (isLocalEnvironment()) {
      console.log(`[META PIXEL] Event skipped (Pixel not initialized): ${eventName}`, params || '');
    }
    return;
  }
  window.fbq('track', eventName, params);
  if (isLocalEnvironment()) {
    console.log(`[META PIXEL] Event sent: ${eventName}`, params || '');
  }
};

/**
 * Initialize Pixel and track PageView if consent is given
 * Integrates with iubenda consent management
 */
export const initPixelWithConsent = (): void => {
  // Check if iubenda consent is available and marketing purpose (5) is accepted
  // Purpose 5 = "Targeting & Advertising" in iubenda
  const checkConsent = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const iubenda = (window as any)._iub;
    if (iubenda?.cs?.consent?.purposes?.[5]) {
      initMetaPixel();
      trackPixelPageView();
      return true;
    }
    return false;
  };

  // Try immediately (user may have already consented)
  if (checkConsent()) return;

  // Listen for consent changes
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const iub = (window as any)._iub || [];
  iub.csConfiguration = iub.csConfiguration || {};
  const existingCallback = iub.csConfiguration.callback;

  iub.csConfiguration.callback = {
    ...existingCallback,
    onConsentGiven: () => {
      existingCallback?.onConsentGiven?.();
      checkConsent();
    },
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any)._iub = iub;
};
